import type { FC } from "react";
import type { Path } from "react-hook-form";

import { useEffect, useId, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { Info } from "lucide-react";

import type { OccupantFormValues } from "../hooks/use-occupant-form-schema";

import { useRequireOptIn } from "../hooks/use-require-opt-in";
import {
  MAX_DESTINATIONS_PER_KIND,
  OCCUPANT_FORM_DEFAULTS,
  useOccupantFormSchema,
} from "../hooks/use-occupant-form-schema";
import { useOccupantService } from "../services/use-occupant-service";
import { useSaveOccupantService } from "../services/use-save-occupant-service";
import { applyServerErrors } from "../utils/apply-server-errors";
import { occupantFormValuesFrom, occupantName } from "../utils/occupant-payload";

import OccupantContactFields from "./occupant-contact-fields";
import OccupantDrawerSkeleton from "./occupant-drawer-skeleton";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";

/** Each destination row's own errors, as the contact endpoints key them. The
 * lists are sent without their blank rows, but a blank row only passes the
 * form's validation when it is the only row of its kind, so the indexes line up. */
const destinationErrorFields = (kind: "phones" | "emails") =>
  Array.from({ length: MAX_DESTINATIONS_PER_KIND }, (_, index) => [
    [`data.attributes.${kind}.${index}.value`, `${kind}.${index}.value` as Path<OccupantFormValues>] as const,
    [`data.attributes.${kind}.${index}.label`, `${kind}.${index}.label` as Path<OccupantFormValues>] as const,
  ]).flat();

/**
 * Maps backend validation error keys → drawer fields.
 *
 * The contact relationship is deliberately absent: it reports under two keys
 * depending on how it failed — `contact_id` when the payload omits it entirely
 * (the rules resolver falls back to the bare property name) and
 * `data.relationships.contact.data.id` when it is present but not reachable from
 * this portal. Neither is a field anyone typed, so both are left unmapped and
 * surface in the banner, where the message ("not available for this contract")
 * actually means something. So does `data.attributes.destination`, an alert row
 * naming a number the contact does not hold.
 */
const OCCUPANT_SERVER_FIELD_MAP: Partial<Record<string, Path<OccupantFormValues>>> = {
  "data.attributes.first_name": "first_name",
  "data.attributes.last_name": "last_name",
  "data.attributes.phone": "phones.0.value",
  "data.attributes.email": "emails.0.value",
  ...Object.fromEntries([...destinationErrorFields("phones"), ...destinationErrorFields("emails")]),
};

interface OccupantDrawerProps {
  contractId: string | number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The contact to edit; omitted to create one. The only thing the drawer
   * needs — everything about that occupant is fetched here, so the caller never
   * hands down values that may have gone stale. */
  contactId?: string;
  /** "Add another" was ticked and the save went through: the drawer clears
   * itself for the next contact, and the caller stops pointing it at the one
   * just edited. */
  onAddAnother?: () => void;
}

/** Which face the drawer is showing: a blank contact being created, or one that
 * already exists reopened for editing. */
type DrawerStep = "create" | "edit";

/**
 * Create / Edit contact drawer. Adding always creates the person here — there is
 * no lookup of contacts already on file — and editing reopens the same form, so
 * a contact is described identically whichever way it was reached.
 */
const OccupantDrawer: FC<OccupantDrawerProps> = ({ contractId, open, onOpenChange, contactId, onAddAnother }) => {
  const { t } = useTranslation(["occupants"]);
  const occupantFormSchema = useOccupantFormSchema();
  const requireOptIn = useRequireOptIn();
  const addAnotherId = useId();

  const step: DrawerStep = contactId ? "edit" : "create";
  // What the server said when the save was refused, for the errors that belong
  // to no single input. The portal has no toaster, so it is shown in the drawer.
  const [saveError, setSaveError] = useState<string | null>(null);
  // "Add another": a successful save clears the form for the next contact
  // instead of closing. Kept across those saves, reset on every open.
  const [addAnother, setAddAnother] = useState(false);
  // What an edit opened with, so the save writes only what changed on the person.
  const initialValues = useRef<OccupantFormValues | undefined>(undefined);

  const methods = useForm<OccupantFormValues>({
    resolver: zodResolver(occupantFormSchema),
    defaultValues: OCCUPANT_FORM_DEFAULTS,
    mode: "onSubmit",
  });

  const occupant = useOccupantService(contractId, contactId, open);
  const occupantContacts = occupant.data?.data ?? [];

  const saveOccupant = useSaveOccupantService(contractId, {
    // Once the person exists, a retry after a failed alert write edits them
    // rather than creating them a second time.
    onContactCreated: (createdId) => methods.setValue("contactId", createdId),
  });

  useEffect(() => {
    if (open) {
      setAddAnother(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSaveError(null);
    initialValues.current = undefined;
    methods.reset(OCCUPANT_FORM_DEFAULTS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contactId]);

  // Hydrates from the fetch, once it lands. `dataUpdatedAt` rather than the data
  // itself: it moves exactly once per fetch, so nothing re-seeds the form — and
  // overwrites what is being typed — while the drawer stays open.
  useEffect(() => {
    if (!open || !contactId || occupantContacts.length === 0) {
      return;
    }

    const hydrated = { ...OCCUPANT_FORM_DEFAULTS, ...occupantFormValuesFrom(occupantContacts) };

    initialValues.current = hydrated;
    methods.reset(hydrated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contactId, occupant.dataUpdatedAt]);

  const startNextContact = () => {
    setSaveError(null);
    initialValues.current = undefined;
    methods.reset(OCCUPANT_FORM_DEFAULTS);
    onAddAnother?.();
  };

  const submit = methods.handleSubmit((values) => {
    setSaveError(null);

    saveOccupant.mutate(
      { values, initial: initialValues.current },
      {
        onSuccess: () => (addAnother ? startNextContact() : onOpenChange(false)),
        onError: (error) => {
          const { mapped, unmapped, message, status } = applyServerErrors(
            error,
            methods.setError,
            OCCUPANT_SERVER_FIELD_MAP,
          );

          // Every message found its own input; a banner repeating them would
          // only say the same thing twice.
          if (mapped > 0 && unmapped.length === 0) {
            setSaveError(null);

            return;
          }

          const fallback = t("occupants:occupants-save-error", "Failed to save the contact");

          setSaveError(
            unmapped.length > 0
              ? unmapped.join(" ")
              : // A failure with no message of its own still names its status —
                // a 404 on the write endpoint and a rejected field are the same
                // sentence otherwise, and only one of them is worth retrying.
                (message ?? (status ? `${fallback} (${status})` : fallback)),
          );
        },
      },
    );
  });

  const titles: Record<DrawerStep, string> = {
    create: t("occupants:occupants-drawer-create-title", "Add a contact"),
    edit: t("occupants:occupants-drawer-edit-title", "Edit contact"),
  };

  const loadingOccupant = step === "edit" && occupant.isPending;
  // The client's own contact: its details are the account information, which the
  // contact endpoint refuses to change from here — only its alert boxes can.
  const isAccountContact =
    step === "edit" && occupantContacts.some((contractContact) => Boolean(contractContact.attributes.is_primary_contact));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* Half the screen is right on a desktop and absurd on a laptop, let alone
          a phone: the panel takes the full width on small screens and only
          settles at 50vw once there is enough room for the destination rows to
          sit side by side. The left rounding goes with it — a full-width sheet
          has no edge to round. */}
      <SheetContent
        className="flex w-full max-w-none! flex-col gap-0 rounded-none bg-card p-0 sm:w-[92vw] sm:rounded-l-2xl lg:w-[70vw] xl:w-[50vw]"
        side="right"
      >
        <SheetHeader className="space-y-1.5 p-5 pb-4 sm:p-8 sm:pb-6">
          <SheetTitle className="flex items-center gap-2">
            {titles[step]}
            {/* Edit names who is being edited, greyed after a divider. */}
            {step === "edit" && (
              <>
                <span className="text-border">|</span>
                <span className="font-normal text-muted-foreground">{occupantName(occupantContacts)}</span>
              </>
            )}
          </SheetTitle>

          {/* Only worth saying when the account actually confirms opt-ins. */}
          {step === "create" && requireOptIn && (
            <SheetDescription className="flex items-center gap-2 text-primary">
              <Info className="h-4 w-4 shrink-0" />
              <Trans
                components={{ strong: <span className="font-semibold" /> }}
                defaults="Contact <strong>will receive</strong> SMS & Email opt-in messages but <strong>won't receive</strong> phone call opt-in message"
                i18nKey="occupants:occupants-drawer-opt-in-notice"
                t={t}
              />
            </SheetDescription>
          )}
        </SheetHeader>

        <FormProvider {...methods}>
          <form className="flex min-h-0 flex-1 flex-col" onSubmit={submit}>
            <div className="flex-1 overflow-y-auto px-5 sm:px-8">
              {saveError && (
                <p className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive" role="alert">
                  {saveError}
                </p>
              )}

              {loadingOccupant ? <OccupantDrawerSkeleton /> : <OccupantContactFields lockDetails={isAccountContact} />}
            </div>

            <div className="flex flex-wrap items-center gap-4 p-5 pt-4 sm:p-8 sm:pt-6">
              <Button
                disabled={saveOccupant.isPending}
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t("occupants:occupants-cancel", "Cancel")}
              </Button>

              <div className="flex flex-1 items-center justify-end gap-4">
                <label className="flex cursor-pointer items-center gap-2.5" htmlFor={addAnotherId}>
                  <Checkbox
                    checked={addAnother}
                    disabled={saveOccupant.isPending}
                    id={addAnotherId}
                    onCheckedChange={(checked) => setAddAnother(checked === true)}
                  />
                  <span className="text-sm font-medium text-foreground">
                    {t("occupants:occupants-add-another", "Add another")}
                  </span>
                </label>

                {/* Nothing typed yet — or nothing changed on the contact being
                    edited — leaves nothing to save. */}
                <Button
                  disabled={saveOccupant.isPending || loadingOccupant || !methods.formState.isDirty}
                  type="submit"
                >
                  {t("occupants:occupants-save", "Save")}
                </Button>
              </div>
            </div>
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default OccupantDrawer;
